import { useState } from 'react';
import type { Consent, ConsentType } from '@repo/domain';
import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import { Button, Modal } from '@repo/ui';

import ConsentToggle from '@shared/components/consent/ConsentToggle';

interface Props {
  testId: TestId;
  consents: Consent[];
  accepting: boolean;
  onAccept: () => Promise<void>;
}

export default function ConsentModal({ testId, consents, accepting, onAccept }: Props) {
  const [acceptedTypes, setAcceptedTypes] = useState<ConsentType[]>([]);

  const allRequiredAccepted = consents
    .filter((consent) => consent.isRequired)
    .every((consent) => acceptedTypes.includes(consent.type));

  const handleToggle = (type: ConsentType, value: boolean) => {
    setAcceptedTypes((previous) => (value ? [...previous, type] : previous.filter((accepted) => accepted !== type)));
  };

  const handleAccept = () => {
    void onAccept();
  };

  return (
    <Modal
      open
      testId={testId}
      width="md"
      dismissableOnClickOutside={false}
      footer={
        <Button
          testId={testId.append('accept')}
          label={tr('reservations:consent_modal.accept_button')}
          color="brand"
          className="w-full"
          disabled={!allRequiredAccepted}
          pending={accepting}
          onClick={handleAccept}
        />
      }
    >
      <div className="flex flex-col gap-4 py-4">
        <h2 className="text-heading text-xl font-semibold">{tr('reservations:consent_modal.title')}</h2>
        <p className="text-body text-sm">{tr('reservations:consent_modal.subtitle')}</p>

        {consents.map((consent) => {
          const handleChange = (value: boolean) => handleToggle(consent.type, value);
          return (
            <ConsentToggle
              key={consent.type}
              consent={consent}
              checked={acceptedTypes.includes(consent.type)}
              onChange={handleChange}
              testId={testId.append('consentCheckbox').append(consent.type)}
            />
          );
        })}
      </div>
    </Modal>
  );
}
